import intersection from 'lodash/intersection';
import merge from 'lodash/merge';
import { removeHighlightText } from '@atlassian/jira-common-util-confluence';
import type { ArticleData } from '@atlassian/jira-servicedesk-knowledge-services';
import type {
	ArticlePersistedState,
	GenericError,
} from '@atlassian/jira-servicedesk-knowledge-services/src/types';
import {
	ADD_CATEGORIES_FOR_ARTICLE,
	type AddCategoriesForArticleAction,
} from '../../actions/add-categories-for-article';
import {
	ADD_CATEGORIES_FOR_ARTICLE_FAILURE,
	type AddCategoriesForArticleFailureAction,
} from '../../actions/add-categories-for-article-failure';
import {
	ADD_CATEGORIES_FOR_ARTICLE_SUCCESS,
	type AddCategoriesForArticleSuccessAction,
} from '../../actions/add-categories-for-article-success';
import {
	ADD_LABELS_TO_ARTICLE,
	type AddLabelsToArticleAction,
} from '../../actions/add-labels-to-article';
import {
	ADD_LABELS_TO_ARTICLE_FAILURE,
	type AddLabelsToArticleFailureAction,
} from '../../actions/add-labels-to-article-failure';
import {
	ADD_LABELS_TO_ARTICLE_SUCCESS,
	type AddLablesToArticleSuccessAction,
} from '../../actions/add-labels-to-article-success';
import {
	BROWSE_KNOWLEDGE_SUCCESS,
	type BrowseKnowledgeSuccessAction,
} from '../../actions/browse-knowledge-success';
import { FETCH_ARTICLE, type FetchArticleAction } from '../../actions/fetch-article';
import {
	FETCH_ARTICLE_FAILURE,
	type FetchArticleFailureAction,
} from '../../actions/fetch-article-failure';
import {
	FETCH_ARTICLE_SUCCESS,
	type FetchArticleSuccessAction,
} from '../../actions/fetch-article-success';
import {
	REMOVE_ARTICLE_FROM_CATEGORY,
	type RemoveArticleFromCategoryAction,
} from '../../actions/remove-article-from-category';
import {
	REMOVE_ARTICLE_FROM_CATEGORY_FAILURE,
	type RemoveArticleFromCategoryFailureAction,
} from '../../actions/remove-article-from-category-failure';
import {
	REMOVE_ARTICLE_FROM_CATEGORY_SUCCESS,
	type RemoveArticleFromCategorySuccessAction,
} from '../../actions/remove-article-from-category-success';
import {
	REMOVE_LABEL_FROM_ARTICLE,
	type RemoveLabelFromArticleAction,
} from '../../actions/remove-label-from-article';
import {
	REMOVE_LABEL_FROM_ARTICLE_FAILURE,
	type RemoveLabelFromArticleFailureAction,
} from '../../actions/remove-label-from-article-failure';
import {
	REMOVE_LABEL_FROM_ARTICLE_SUCCESS,
	type RemoveLabelFromArticleSuccessAction,
} from '../../actions/remove-label-from-article-success';
import {
	SEARCH_ARTICLES_SUCCESS,
	type SearchArticlesSuccessAction,
} from '../../actions/search-articles-success';
import {
	type SetArticleEditPermissionAction,
	SET_ARTICLE_EDIT_PERMISSION,
} from '../../actions/set-article-edit-permission';
import {
	UPDATE_CATEGORIES_FOR_ARTICLE,
	type UpdateCategoriesForArticleAction,
} from '../../actions/update-categories-for-article';
import {
	UPDATE_CATEGORIES_FOR_ARTICLE_FAILURE,
	type UpdateCategoriesForArticleFailureAction,
} from '../../actions/update-categories-for-article-failure';
import {
	UPDATE_CATEGORIES_FOR_ARTICLE_SUCCESS,
	type UpdateCategoriesForArticleSuccessAction,
} from '../../actions/update-categories-for-article-success';

import type { ArticlesState } from './types';

type Action =
	| AddCategoriesForArticleAction
	| AddCategoriesForArticleFailureAction
	| AddCategoriesForArticleSuccessAction
	| RemoveArticleFromCategoryAction
	| RemoveArticleFromCategoryFailureAction
	| RemoveArticleFromCategorySuccessAction
	| BrowseKnowledgeSuccessAction
	| SearchArticlesSuccessAction
	| FetchArticleAction
	| FetchArticleSuccessAction
	| FetchArticleFailureAction
	| UpdateCategoriesForArticleAction
	| UpdateCategoriesForArticleFailureAction
	| UpdateCategoriesForArticleSuccessAction
	| AddLablesToArticleSuccessAction
	| RemoveLabelFromArticleSuccessAction
	| AddLabelsToArticleFailureAction
	| AddLabelsToArticleAction
	| RemoveLabelFromArticleAction
	| RemoveLabelFromArticleFailureAction
	| SetArticleEditPermissionAction;

export const defaultState: ArticlesState = {};

const articleListToObject = (articles: ArticleData[]): ArticlesState =>
	articles.reduce<ArticlesState>(
		(acc, article) => ({
			// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
			...acc,
			[article.id]: {
				...article,
				// This is required to handle the response provided
				// by the legacy JSD endpoint.
				// We can remove this when browseAllArticlesMilestone1
				// is unfeatured.
				title: removeHighlightText(article.title),
				categories: article.categories && article.categories.map((category) => category.id),
			},
		}),
		{},
	);

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (state: ArticlesState = defaultState, action: Action): ArticlesState => {
	switch (action.type) {
		case REMOVE_ARTICLE_FROM_CATEGORY:
		case ADD_CATEGORIES_FOR_ARTICLE:
		case UPDATE_CATEGORIES_FOR_ARTICLE: {
			if (state[action.payload.articleId]) {
				return {
					...state,
					[action.payload.articleId]: {
						...state[action.payload.articleId],
						isBusy: true,
					},
				};
			}

			return state;
		}

		case REMOVE_ARTICLE_FROM_CATEGORY_FAILURE:
		case ADD_CATEGORIES_FOR_ARTICLE_FAILURE:
		case UPDATE_CATEGORIES_FOR_ARTICLE_FAILURE: {
			if (state[action.payload.articleId]) {
				return {
					...state,
					[action.payload.articleId]: {
						...state[action.payload.articleId],
						isBusy: false,
					},
				};
			}

			return state;
		}

		case REMOVE_ARTICLE_FROM_CATEGORY_SUCCESS: {
			if (state[action.payload.articleId]) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const existingArticle = state[action.payload.articleId] as ArticlePersistedState;
				const { categoryId } = action.payload;
				const newCategoriesList = (existingArticle.categories || []).filter(
					(id) => id !== categoryId,
				);

				const featuredInCategoryList = (existingArticle.featuredInCategories || []).filter(
					(id) => id !== categoryId,
				);

				return {
					...state,
					[action.payload.articleId]: {
						...existingArticle,
						categories: newCategoriesList,
						featuredInCategories: featuredInCategoryList,
						isBusy: false,
					},
				};
			}

			return state;
		}

		case ADD_CATEGORIES_FOR_ARTICLE_SUCCESS: {
			const { articleId, categoryIds: newlyAddedCategoryIds, previousCategoryIds } = action.payload;
			const allCategoryIds = [...previousCategoryIds, ...newlyAddedCategoryIds];
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const existingArticle = state[articleId] as ArticlePersistedState;
			if (existingArticle) {
				const existingFeaturedInCategoryIds = existingArticle.featuredInCategories || [];
				// only keep featured in categories list with update categories
				const featuredInCategories = intersection(allCategoryIds, existingFeaturedInCategoryIds);

				return {
					...state,
					[articleId]: {
						...existingArticle,
						featuredInCategories,
						categories: allCategoryIds,
						isBusy: false,
					},
				};
			}
			return state;
		}

		case UPDATE_CATEGORIES_FOR_ARTICLE_SUCCESS: {
			const { articleId, categoryIds } = action.payload;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const existingArticle = state[articleId] as ArticlePersistedState;
			if (existingArticle) {
				const existingFeaturedInCategoryIds = existingArticle.featuredInCategories || [];
				// only keep featured in categories list with update categories
				const featuredInCategories = intersection(categoryIds, existingFeaturedInCategoryIds);

				return {
					...state,
					[articleId]: {
						...existingArticle,
						featuredInCategories,
						categories: categoryIds,
						isBusy: false,
					},
				};
			}
			return state;
		}

		case BROWSE_KNOWLEDGE_SUCCESS:
			return merge({}, state, articleListToObject(action.payload.results));

		case SEARCH_ARTICLES_SUCCESS:
			return merge({}, state, articleListToObject(action.payload));

		case FETCH_ARTICLE: {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			if (state[action.payload.id] && (state[action.payload.id] as GenericError).error) {
				const newState = {
					...state,
				};

				// Remove the article from state if it had an error when fetching again
				delete newState[action.payload.id];

				return newState;
			}

			return state;
		}

		case FETCH_ARTICLE_SUCCESS: {
			const { payload } = action;
			const categories = payload.categories
				? { categories: payload.categories.map((category) => category.id) }
				: {};
			const featuredInCategories = payload.featuredInCategories
				? { featuredInCategories: payload.featuredInCategories }
				: {};
			const labels = payload.labels?.length
				? payload.labels.map(({ id, label, name }) => ({ id, label, name }))
				: [];
			const labelState = {
				labels,
				isLoading: false,
				addLabelError: false,
			};
			const { labels: existingLabels, isEditable, ...payloadWithoutLabels } = payload;
			const articleState = {
				...payloadWithoutLabels,
				...categories,
				...featuredInCategories,
				labelState,
				isEditable: false,
			};

			return {
				...state,
				[action.payload.id]: merge({}, state[action.payload.id], articleState),
			};
		}

		case FETCH_ARTICLE_FAILURE:
			return {
				...state,
				[action.payload.id]: {
					error: action.payload.error,
				},
			};

		case ADD_LABELS_TO_ARTICLE_SUCCESS: {
			const { articleId, labels } = action.payload;
			const existingArticle = state[articleId];
			if (existingArticle) {
				const updatedLabels = labels.length
					? labels.map(({ id, label, name }) => ({ id, label, name }))
					: [];

				const labelState = {
					labels: updatedLabels,
					isLoading: false,
					addLabelError: false,
				};

				return {
					...state,
					[articleId]: {
						...existingArticle,
						labelState,
					},
				};
			}
			return state;
		}

		case REMOVE_LABEL_FROM_ARTICLE_SUCCESS: {
			const { label, articleId } = action.payload;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const existingArticle = state[articleId] as ArticlePersistedState;
			if (existingArticle) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const labelState = (existingArticle.labelState || {}) as NonNullable<
					ArticlePersistedState['labelState']
				>;
				const existingLabels = labelState.labels || [];
				const updatedLabels = existingLabels?.length
					? existingLabels.filter(({ label: existingLabel }) => label !== existingLabel)
					: [];

				return {
					...state,
					[articleId]: {
						...existingArticle,
						labelState: {
							labels: updatedLabels,
							isLoading: false,
							addLabelError: false,
						},
					},
				};
			}
			return state;
		}

		case ADD_LABELS_TO_ARTICLE_FAILURE: {
			const { articleId } = action.payload;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const existingArticle = state[articleId] as ArticlePersistedState;
			if (existingArticle && existingArticle.labelState) {
				const existingLabels = existingArticle.labelState.labels || [];
				const labelState = {
					labels: existingLabels,
					isLoading: false,
					addLabelError: true,
				};

				return {
					...state,
					[articleId]: {
						...existingArticle,
						labelState,
					},
				};
			}
			return state;
		}
		case REMOVE_LABEL_FROM_ARTICLE:
		case ADD_LABELS_TO_ARTICLE: {
			const { articleId } = action.payload;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const existingArticle = state[articleId] as ArticlePersistedState;
			if (existingArticle && existingArticle.labelState) {
				const existingLabels = existingArticle.labelState.labels || [];
				const labelState = {
					labels: existingLabels,
					isLoading: true,
					addLabelError: false,
				};

				return {
					...state,
					[articleId]: {
						...existingArticle,
						labelState,
					},
				};
			}
			return state;
		}

		case REMOVE_LABEL_FROM_ARTICLE_FAILURE: {
			const { articleId } = action.payload;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const existingArticle = state[articleId] as ArticlePersistedState;
			if (existingArticle && existingArticle.labelState) {
				const existingLabels = existingArticle.labelState.labels || [];
				const labelState = {
					labels: existingLabels,
					isLoading: false,
					addLabelError: false,
				};

				return {
					...state,
					[articleId]: {
						...existingArticle,
						labelState,
					},
				};
			}
			return state;
		}

		case SET_ARTICLE_EDIT_PERMISSION: {
			const { articleId, isEditable } = action.payload;
			const existingArticle = state[articleId];
			if (existingArticle) {
				return {
					...state,
					[articleId]: {
						...existingArticle,
						isEditable,
					},
				};
			}
			return state;
		}
		default: {
			return state;
		}
	}
};
